import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { Send } from 'lucide-react';

const Messages = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState({});
  const [activeThread, setActiveThread] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_BASE}/messages/${user.id}`);
      setThreads(response.data);
      if (Object.keys(response.data).length > 0) {
        setActiveThread(Object.keys(response.data)[0]);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeThread) return;

    try {
      const messages = threads[activeThread];
      const lastMessage = messages[0];
      const otherUserId = lastMessage.from_user_id === user.id ? lastMessage.to_user_id : lastMessage.from_user_id;

      await axios.post(`${API_BASE}/messages`, {
        from_user_id: user.id,
        to_user_id: otherUserId,
        thread_id: activeThread,
        content: newMessage
      });

      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  if (loading) {
    return <div className="p-8 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D9488]"></div></div>;
  }

  const threadIds = Object.keys(threads);

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>Messages</h1>

        {threadIds.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <p className="text-slate-500">No messages yet. Start connecting with employers!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Thread List */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
              {threadIds.map((threadId) => {
                const messages = threads[threadId];
                const lastMessage = messages[0];
                return (
                  <button
                    key={threadId}
                    onClick={() => setActiveThread(threadId)}
                    className={`w-full text-left p-4 rounded-lg transition-colors ${
                      activeThread === threadId
                        ? 'bg-[#0D9488] text-white'
                        : 'hover:bg-slate-100'
                    }`}
                    data-testid={`thread-${threadId}`}
                  >
                    <div className="font-semibold text-sm">Thread {threadId.slice(-8)}</div>
                    <div className="text-xs opacity-80 truncate">{lastMessage.content}</div>
                  </button>
                );
              })}
            </div>

            {/* Message View */}
            <div className="md:col-span-2 bg-white border border-slate-200 rounded-2xl flex flex-col">
              <div className="flex-1 p-6 space-y-4 overflow-y-auto max-h-[500px]">
                {activeThread && threads[activeThread].slice().reverse().map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.from_user_id === user.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs px-4 py-3 rounded-2xl ${
                        message.from_user_id === user.id
                          ? 'bg-[#0D9488] text-white'
                          : 'bg-slate-100 text-slate-900'
                      }`}
                    >
                      {message.content}
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-slate-200">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button
                    onClick={sendMessage}
                    className="bg-[#0D9488] hover:bg-[#115E59] rounded-full"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
